import React, { useAtom } from "jotai";
import toast from "react-hot-toast";
import classNames from "classnames";
import { Link } from "react-router-dom";
import { UserInfo } from "interfaces/user";
import Button from "components/base/Button";
import llmAxiosInstance from "helpers/llmApi";
import { Card, Spinner, Badge } from "react-bootstrap";
import { user_info } from "jotai/UserJotaiInstance";
import { useEffect, useRef, useState } from "react";
import { entityTypes, formatErrorForReact } from "helpers/utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBrain, faPaperPlane, faRocket, faStar, faChartLine } from "@fortawesome/free-solid-svg-icons";

interface Message {
  id: number;
  text: string;
  sender: "user" | "ai";
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const baseURL = "http://localhost:5000/api/";

// Create a more flexible interface that accepts optional fields
type FlexibleDealProps = {
  id?: string | number;
  deal_name?: string;
  deal_price?: number;
  deal_state?: string;
  deal_lead_status?: string;
  deal_lead_type?: string;
  deal_closing_date?: string;
  deal_owner_id?: string;
  created_on?: string;
  deal_mls_id?: string;
  customers?: any[];
  related_deals?: any[];
  deal_inspection_period?: string;
  deal_meetings?: any[];
};

const DealLLMContentSection = ({ className, dealDetails }: { className?: string, dealDetails?: FlexibleDealProps }) => {
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const [userInfo] = useAtom<UserInfo>(user_info);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 1,
      text: dealDetails 
        ? `Hello! I can help you with information about the ${dealDetails.deal_name || 'selected'} deal. What would you like to know?` 
        : "Hello! Welcome to KAIROS IQ. How can I help you?",
      sender: "ai",
    },
  ]);
  const [inputMessage, setInputMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) {
      setError("Message cannot be empty");
      return;
    }

    setError("");

    const dealId = dealDetails?.id || "01jqzr5dzbq4e3nkwpyaexd4dv"; // Use dealDetails.id if available, otherwise fallback
    const userId = userInfo?.id || "01jqzr1m1q7sdw5q8mtnj6z3rr";

    setLoading(true);
    const newUserMessage: Message = {
      id: messages.length + 1,
      text: inputMessage,
      sender: "user",
    };

    setMessages((prevMessages) => [...prevMessages, newUserMessage]);

    try {
      setInputMessage("");
      const response = await llmAxiosInstance.post("/chat", {
        entity_id: dealId,
        entity_type: entityTypes.deal,
        user_id: userId,
        message_text: inputMessage,
        conversation_id: localStorage.getItem("conversationId") || undefined,
      });

      // Validate response structure
      if (!response || !response.data) {
        throw new Error("Invalid response from AI service");
      }

      const aiResponse: Message = {
        id: messages.length + 2,
        text: response.data.response_text || "Sorry, I couldn't process your request.",
        sender: "ai",
      };

      setMessages((prevMessages) => [...prevMessages, aiResponse]);
      setLoading(false);
    } catch (error: any) {
      console.error("AI Chat Error:", error);
      
      // Better error handling
      let errorMessage = "Failed to send message. Please try again.";
      
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
      
      // Add error message to chat
      const errorResponse: Message = {
        id: messages.length + 2,
        text: "Sorry, I'm having trouble processing your request right now. Please try again later.",
        sender: "ai",
      };
      
      setMessages((prevMessages) => [...prevMessages, errorResponse]);
      setLoading(false);
    }
  };

  return (
    <div className={classNames('modern-deal-llm-section', className)}>
      <Card className="deal-ai-card border-0">
        <Card.Body className="p-4">
          <div className="ai-header mb-4">
            <div className="d-flex align-items-center gap-3 mb-2">
              <div className="ai-icon-wrapper">
                <FontAwesomeIcon icon={faBrain} className="ai-icon" />
              </div>
              <div className="flex-grow-1">
                <h5 className="ai-title mb-1">
                  {dealDetails ? `${dealDetails.deal_name || 'Deal'} AI Assistant` : 'Deal AI Assistant'}
                </h5>
                <p className="ai-subtitle mb-0">Get insights, analysis, and recommendations</p>
              </div>
              <Badge className="ai-badge">
                <FontAwesomeIcon icon={faStar} className="me-1" />
                AI Powered
              </Badge>
            </div>
          </div>

          {userInfo?.user_subscription?.subscription_package
            ?.subscription_package_name !== "Essential" ? (
            <>
              <div className="chat-section">
                <div
                  ref={chatContainerRef}
                  className="chat-messages"
                >
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={classNames(
                        "message-wrapper",
                        message.sender === "user" ? "user-message" : "ai-message"
                      )}
                    >
                      <div className="message-content">
                        {message.sender === "ai" && (
                          <div className="message-avatar ai-avatar">
                            <FontAwesomeIcon icon={faBrain} />
                          </div>
                        )}
                        <div className="message-bubble">
                          <p className="message-text mb-0">{message.text}</p>
                        </div>
                        {message.sender === "user" && (
                          <div className="message-avatar user-avatar">
                            <span>You</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {loading && (
                    <div className="message-wrapper ai-message">
                      <div className="message-content">
                        <div className="message-avatar ai-avatar">
                          <FontAwesomeIcon icon={faBrain} />
                        </div>
                        <div className="message-bubble typing">
                          <div className="typing-indicator">
                            <span></span>
                            <span></span>
                            <span></span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="chat-input-section">
                  <div className="input-wrapper">
                    <input
                      type="text"
                      className="chat-input"
                      placeholder={dealDetails ? `Ask about ${dealDetails.deal_name || 'this deal'}...` : "Ask about deals..."}
                      value={inputMessage}
                      onChange={(e) => setInputMessage(e.target.value)}
                      onKeyPress={(e) => e.key === "Enter" && handleSendMessage()}
                      disabled={loading}
                    />
                    <Button
                      className="send-button"
                      onClick={handleSendMessage}
                      disabled={loading || !inputMessage.trim()}
                      variant="primary"
                    >
                      {loading ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        <FontAwesomeIcon icon={faPaperPlane} />
                      )}
                    </Button>
                  </div>
                  {error && (
                    <div className="error-message">
                      <span className="text-danger">{formatErrorForReact(error)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Quick Suggestions */}
              <div className="quick-suggestions">
                <h6 className="suggestions-title">Quick Questions</h6>
                <div className="suggestion-buttons">
                  <Button 
                    size="sm" 
                    variant="outline-primary"
                    className="suggestion-btn"
                    onClick={() => setInputMessage("What's the deal status?")}
                  >
                    <FontAwesomeIcon icon={faChartLine} className="me-1" />
                    Deal Status
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline-primary"
                    className="suggestion-btn"
                    onClick={() => setInputMessage("Show me deal insights")}
                  >
                    <FontAwesomeIcon icon={faBrain} className="me-1" />
                    Insights
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="upgrade-section">
              <div className="upgrade-content">
                <div className="upgrade-icon">
                  <FontAwesomeIcon icon={faRocket} />
                </div>
                <h6 className="upgrade-title">Unlock AI Insights</h6>
                <p className="upgrade-text">
                  Get AI-powered deal analysis, recommendations, and insights to close more deals faster.
                </p>
                <Button
                  as={Link}
                  to="/settings/subscription-settings"
                  variant="primary"
                  className="upgrade-btn"
                >
                  <FontAwesomeIcon icon={faRocket} className="me-2" />
                  Upgrade Now
                </Button>
              </div>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Enhanced Styles */}
      <style dangerouslySetInnerHTML={{
        __html: `
        .modern-deal-llm-section {
          --ai-primary: #0d6efd;
          --ai-success: #198754;
          --ai-info: #0dcaf0;
          --ai-secondary: #6c757d;
          --ai-light: #f8f9fa;
          --ai-dark: #212529;
          --ai-border: #e9ecef;
          --ai-shadow: 0 4px 20px rgba(0,0,0,0.08);
          --ai-gradient: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        }

        .deal-ai-card {
          background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
          border-radius: 16px !important;
          box-shadow: var(--ai-shadow);
          transition: all 0.3s ease;
          overflow: hidden;
          position: relative;
        }

        .deal-ai-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: var(--ai-gradient);
        }

        .ai-header {
          border-bottom: 1px solid var(--ai-border);
          padding-bottom: 1rem;
        }

        .ai-icon-wrapper {
          width: 48px;
          height: 48px;
          background: var(--ai-gradient);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.25);
        }

        .ai-icon {
          color: white;
          font-size: 1.2rem;
        }

        .ai-title {
          font-size: 1.1rem;
          font-weight: 700;
          color: var(--ai-dark);
          margin: 0;
        }

        .ai-subtitle {
          font-size: 0.85rem;
          color: var(--ai-secondary);
          margin: 0;
        }

        .ai-badge {
          background: var(--ai-gradient) !important;
          color: white !important;
          border: none !important;
          padding: 0.5rem 0.75rem !important;
          border-radius: 20px !important;
          font-size: 0.75rem !important;
          font-weight: 500 !important;
        }

        .chat-section {
          margin-bottom: 1rem;
        }

        .chat-messages {
          height: 300px;
          overflow-y: auto;
          padding: 1rem;
          background: var(--ai-light);
          border-radius: 12px;
          margin-bottom: 1rem;
          scrollbar-width: thin;
          scrollbar-color: var(--ai-border) var(--ai-light);
        }

        .chat-messages::-webkit-scrollbar {
          width: 6px;
        }

        .chat-messages::-webkit-scrollbar-track {
          background: var(--ai-light);
        }

        .chat-messages::-webkit-scrollbar-thumb {
          background: var(--ai-border);
          border-radius: 3px;
        }

        .message-wrapper {
          margin-bottom: 1rem;
        }

        .message-wrapper.ai-message .message-content {
          justify-content: flex-start;
        }

        .message-wrapper.user-message .message-content {
          justify-content: flex-end;
        }

        .message-content {
          display: flex;
          align-items: flex-end;
          gap: 0.5rem;
        }

        .message-avatar {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.7rem;
          font-weight: 600;
          flex-shrink: 0;
        }

        .ai-avatar {
          background: var(--ai-gradient);
          color: white;
        }

        .user-avatar {
          background: var(--ai-primary);
          color: white;
          font-size: 0.6rem;
        }

        .message-bubble {
          max-width: 280px;
          padding: 0.75rem 1rem;
          border-radius: 16px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          position: relative;
        }

        .ai-message .message-bubble {
          background: white;
          border-bottom-left-radius: 4px;
        }

        .user-message .message-bubble {
          background: var(--ai-primary);
          color: white;
          border-bottom-right-radius: 4px;
        }

        .message-text {
          font-size: 0.9rem;
          line-height: 1.4;
          margin: 0;
        }

        .typing {
          background: white !important;
          padding: 1rem !important;
        }

        .typing-indicator {
          display: flex;
          gap: 4px;
          align-items: center;
        }

        .typing-indicator span {
          width: 6px;
          height: 6px;
          background: var(--ai-secondary);
          border-radius: 50%;
          animation: typing 1.4s infinite ease-in-out;
        }

        .typing-indicator span:nth-child(1) {
          animation-delay: -0.32s;
        }

        .typing-indicator span:nth-child(2) {
          animation-delay: -0.16s;
        }

        @keyframes typing {
          0%, 80%, 100% {
            transform: scale(0.8);
            opacity: 0.5;
          }
          40% {
            transform: scale(1);
            opacity: 1;
          }
        }

        .chat-input-section {
          border-top: 1px solid var(--ai-border);
          padding-top: 1rem;
        }

        .input-wrapper {
          display: flex;
          gap: 0.5rem;
          align-items: center;
        }

        .chat-input {
          flex: 1;
          border: 1px solid var(--ai-border);
          border-radius: 12px;
          padding: 0.75rem 1rem;
          font-size: 0.9rem;
          transition: all 0.2s ease;
        }

        .chat-input:focus {
          outline: none;
          border-color: var(--ai-primary);
          box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.1);
        }

        .send-button {
          width: 44px;
          height: 44px;
          border-radius: 12px !important;
          display: flex;
          align-items: center;
          justify-content: center;
          border: none !important;
          background: var(--ai-primary) !important;
          transition: all 0.2s ease !important;
        }

        .send-button:hover:not(:disabled) {
          background: var(--ai-primary) !important;
          transform: scale(1.05);
          box-shadow: 0 4px 12px rgba(13, 110, 253, 0.3);
        }

        .send-button:disabled {
          opacity: 0.5;
          transform: none !important;
        }

        .error-message {
          margin-top: 0.5rem;
          font-size: 0.8rem;
        }

        .quick-suggestions {
          border-top: 1px solid var(--ai-border);
          padding-top: 1rem;
        }

        .suggestions-title {
          font-size: 0.85rem;
          font-weight: 600;
          color: var(--ai-secondary);
          margin-bottom: 0.5rem;
        }

        .suggestion-buttons {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
        }

        .suggestion-btn {
          font-size: 0.8rem !important;
          padding: 0.4rem 0.75rem !important;
          border-radius: 20px !important;
          border: 1px solid var(--ai-primary) !important;
          transition: all 0.2s ease !important;
        }

        .suggestion-btn:hover {
          background: var(--ai-primary) !important;
          color: white !important;
          transform: translateY(-1px);
        }

        .upgrade-section {
          text-align: center;
          padding: 2rem 1rem;
        }

        .upgrade-content {
          max-width: 280px;
          margin: 0 auto;
        }

        .upgrade-icon {
          width: 64px;
          height: 64px;
          background: var(--ai-gradient);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1rem;
          font-size: 1.5rem;
          color: white;
        }

        .upgrade-title {
          font-size: 1.1rem;
          font-weight: 700;
          color: var(--ai-dark);
          margin-bottom: 0.5rem;
        }

        .upgrade-text {
          font-size: 0.9rem;
          color: var(--ai-secondary);
          margin-bottom: 1.5rem;
          line-height: 1.4;
        }

        .upgrade-btn {
          background: var(--ai-gradient) !important;
          border: none !important;
          border-radius: 12px !important;
          padding: 0.75rem 1.5rem !important;
          font-weight: 600 !important;
          transition: all 0.2s ease !important;
        }

        .upgrade-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(102, 126, 234, 0.3);
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .chat-messages {
            height: 250px;
          }

          .message-bubble {
            max-width: 220px;
          }

          .ai-header {
            flex-direction: column;
            align-items: center;
            text-align: center;
          }

          .suggestion-buttons {
            justify-content: center;
          }
        }
        `
      }} />
    </div>
  );
};

export default DealLLMContentSection;
